import type { AxiosResponse } from "axios";
import type { CSVImportData } from "csv-import-js";
import type { SerializedEditorState } from "lexical";

import { AUDIO_MIME_TYPE } from "~/constants/speechToText";
import {
  EventKind,
  type FeedbackRating,
  type Language,
  type RecommendationKind,
  ReportKind,
  TaskSourceKind,
  type TutorialName,
  UserRole,
} from "~/shared/enums";
import type {
  AttachmentCreate,
  ChartDataResponse,
  ChatMessage,
  Comment,
  EmailAuthState,
  EventAdtl,
  FilterGroup,
  GithubIntegrationEdit,
  NotificationSettingEdit,
  PaginatedAttachmentList,
  PaginatedCommentList,
  PaginatedDocList,
  PaginatedNotificationList,
  PaginatedTaskList,
  Sort,
  StreamCallback,
  SummaryStatisticsResponse,
  Task,
  TaskCreate,
  TaskField,
  TaskRelationshipQuery,
} from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import { downloadFile } from "~/utils/api";

import { requestManager } from "./requestManager";
import { DartError } from "./shared";

type Response<T> = Promise<AxiosResponse<T>>;

const BASE_URL = "/api/v0";

const logTime = async <T>(message: string, promiseFunc: () => Promise<T>): Promise<T> => {
  const result = await promiseFunc();
  usePageStore().logTime(message, performance.now());
  return result;
};

const paginatedUrl = (
  path: string,
  offset: number,
  limit?: number,
  params?: Record<string, string | boolean | null | undefined>
) => {
  const inner = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === null) {
      inner.append(key, "");
      return;
    }
    if (value !== undefined) {
      inner.append(key, value.toString());
    }
  });

  inner.append("offset", offset.toString());
  if (limit) {
    inner.append("limit", limit.toString());
  }
  return `${path}?${inner.toString()}`;
};

// TODO convert to class and make underscored methods TS private with # instead of _
const backendOld = {
  _checkAuth: (authOverride: boolean | undefined, method: string, path: string) => {
    const pageStore = usePageStore();

    if (pageStore.isPublic && authOverride !== true) {
      throw new DartError(`Cannot make ${method} request to ${path} in public view`);
    }
  },
  _get: (
    path: string,
    options?: { authOverride?: boolean; absolute?: boolean; nonBlocking?: boolean; background?: boolean }
  ) => {
    backendOld._checkAuth(options?.authOverride, "GET", path);
    return requestManager.get(
      options?.absolute ? path : `${BASE_URL}/${path}`,
      options?.nonBlocking,
      options?.background
    );
  },
  _put: (
    path: string,
    data?: object,
    options?: { authOverride?: boolean; absolute?: boolean; nonBlocking?: boolean },
    headers?: Record<string, string>,
    onStream?: StreamCallback
  ) => {
    backendOld._checkAuth(options?.authOverride, "PUT", path);
    return requestManager.put(
      options?.absolute ? path : `${BASE_URL}/${path}`,
      options?.nonBlocking,
      data,
      headers,
      onStream
    );
  },
  _post: async (
    path: string,
    data?: object,
    options?: { authOverride?: boolean; absolute?: boolean; nonBlocking?: boolean; background?: boolean },
    headers?: Record<string, string>,
    onStream?: StreamCallback
  ) => {
    backendOld._checkAuth(options?.authOverride, "GET", path);
    return requestManager.post(
      options?.absolute ? path : `${BASE_URL}/${path}`,
      options?.nonBlocking,
      data,
      headers,
      onStream,
      options?.background
    );
  },
  _downloadFileRaw: (path: string) => downloadFile(`${BASE_URL}/${path}`),
  setCsrf: async () => {
    const pageStore = usePageStore();
    pageStore.updateCsrf();
    if (pageStore.csrftoken) {
      return;
    }

    await backendOld._get("csrf-token", { authOverride: true });
    pageStore.updateCsrf();
  },
  ping: () => backendOld._get("ping"),
  log: (toDatabase: boolean, kind: string, message: string | object) =>
    backendOld._post("log", { toDatabase, datetime: new Date().toISOString(), kind, message }),
  createEvent: (kind: EventKind, adtl?: EventAdtl, nonBlocking?: boolean) =>
    backendOld._post("create-event", { kind, adtl }, { authOverride: true, nonBlocking }),
  createPresignedUrl: (kind: string, filename: string, entityDuid?: string, formDuid?: string) =>
    backendOld._post("create-presigned-url", { kind, filename, entityDuid, formDuid }, { authOverride: true }),
  createTestError: () => backendOld._post("create-test-error"),
  getUserData: () => logTime("getting user data", () => backendOld._get("user-data", { authOverride: true })),
  auth: {
    checkInvitationToken: (tokenDuid: string) => backendOld._get(`check-invitation-token?duid=${tokenDuid}`),
    checkReferralTokenAndMaybeCreateAndLogin: (tokenId: string) =>
      backendOld._get(`check-referral-token-and-maybe-create-and-login?tokenId=${tokenId}`),
    checkResetPasswordToken: (tokenDuid: string) => backendOld._get(`check-reset-password-token?duid=${tokenDuid}`),
    login: (email: string, password: string) =>
      logTime("logging in and getting user data", () => backendOld._post("login", { email, password })),
    getSamlSsoRedirect: (email: string) => backendOld._get(`sso/saml-redirect/${email}`),
    tokenLogin: (tokenDuid: string) =>
      logTime("logging in with token and getting user data", () =>
        backendOld._post("token-login", { duid: tokenDuid })
      ),
    acceptInvitation: (tokenDuid: string, name: string, password: string) =>
      logTime("accepting invitation and getting user data", () =>
        backendOld._post("accept-invitation", { tokenDuid, name, password })
      ),
    signUp: (
      email: string,
      name: string,
      password: string,
      tokenId: string,
      gtagUserId: string | null,
      appSumoCode: string | null,
      state: EmailAuthState
    ) => backendOld._post("sign-up", { email, name, password, tokenId, gtagUserId, appSumoCode, state }),
    verifyEmail: (tokenDuid: string) =>
      logTime("verifying email and getting user data", () => backendOld._post("verify-email", { tokenDuid })),
    requestPasswordReset: (email: string) => backendOld._post("request-password-reset", { email }),
    changePassword: (tokenDuid: string, newPassword: string) =>
      backendOld._post("change-password", { tokenDuid, password: newPassword }),
    authenticateWithGoogle: (
      code: string,
      redirectUri: string,
      tokenId: string | null,
      gtagUserId: string | null,
      appSumoCode: string | null
    ) => backendOld._post("authenticate-with-google", { code, redirectUri, tokenId, gtagUserId, appSumoCode }),
    getEnvironment: () => backendOld._get("environment", { authOverride: true }),
    logout: () => backendOld._post("logout"),
    makeTeammateLoginTokenAdmin: (duid?: string) =>
      backendOld._post("make-teammate-login-token-admin", { duid: duid ?? null }),
    getPreviewTitlesDart: () => backendOld._get("preview-titles-dart"),
    sendDesktopLinkEmail: () => backendOld._post("send-desktop-link-email"),
  },
  agents: {
    rotateAuthToken: (agentDuid: string) => backendOld._post("agents/rotate-auth-token", { agentDuid }),
    deleteAuthToken: (agentDuid: string) => backendOld._post("agents/delete-auth-token", { agentDuid }),
    uploadImage: async (agentDuid: string, file: File) => {
      const response = await backendOld.createPresignedUrl("profile", file.name);
      await backendOld._put(response.data.presignedUrl, file, { absolute: true }, response.data.signedHeaders);
      await backendOld._post("agents/complete-image-upload", {
        agentDuid,
        imagePath: response.data.filePath,
        contentType: file.type,
      });
      return response;
    },
    removeImage: (agentDuid: string) => backendOld._post("agents/remove-image", { agentDuid }),
    rotateWebhook: (agentDuid: string) => backendOld._post("agents/rotate-webhook", { agentDuid }),
    deleteWebhook: (agentDuid: string) => backendOld._post("agents/delete-webhook", { agentDuid }),
  },
  appSumo: {
    redeemLicense: (code: string) => backendOld._post("app-sumo/redeem-license", { code }),
  },
  dartboards: {
    getBySpace: (spaceDuid: string) => backendOld._get(`dartboards/get-by-space/${spaceDuid}`),
    replicate: (dartboardDuid: string, options: { targetDuid?: string }) =>
      backendOld._post(`dartboards/replicate/${dartboardDuid}`, options),
  },
  dashboards: {
    getDartboardStats: (dartboardDuid: string) =>
      backendOld._get(`dashboards/get-dartboard-stats/${dartboardDuid}`, { background: true }),
    getChartData: (dashboardDuid: string): Response<ChartDataResponse> =>
      backendOld._get(`dashboards/get-chart-data/${dashboardDuid}`, { background: true }),
  },
  views: {
    getData: (viewDuid: string, all: boolean) =>
      backendOld._get(`views/get-data/${viewDuid}?all=${all}`, { authOverride: true }),
    importTemplateView: (viewDuid: string, dartboardDuid: string, propertyDuidRemap: Record<string, string>) =>
      backendOld._post("views/import-template-view", { viewDuid, dartboardDuid, propertyDuidRemap }),
  },
  docs: { getByFolder: (folderDuid: string) => backendOld._get(`docs/get-by-folder/${folderDuid}`) },
  folders: {
    getBySpace: (spaceDuid: string) => backendOld._get(`folders/get-by-space/${spaceDuid}`),
    replicate: (folderDuid: string, options: { targetDuid?: string }) =>
      backendOld._post(`folders/replicate/${folderDuid}`, options),
  },
  forms: { getData: (formDuid: string) => backendOld._get(`forms/get-data/${formDuid}`, { authOverride: true }) },
  tasks: {
    createFromExternalForm: (task: TaskCreate, attachments: AttachmentCreate[]) =>
      backendOld._post(
        "tasks/create-from-external-form",
        { task: { ...task, sourceKind: TaskSourceKind.EXTERNAL_FORM }, attachments },
        { authOverride: true }
      ),
    getByDartboard: (dartboardDuid: string) => backendOld._get(`tasks/get-by-dartboard/${dartboardDuid}`),
    addNotionDocument: (taskDuid: string, pageId: string) =>
      backendOld._post("tasks/add-notion-document", { taskDuid, pageId }),
    getNotionDocument: (taskDuid: string) => backendOld._get(`tasks/get-notion-document/${taskDuid}`),
    refreshNotionDocument: (taskDuid: string) => backendOld._post("tasks/refresh-notion-document", { taskDuid }),
    removeNotionDocument: (taskDuid: string) => backendOld._post("tasks/remove-notion-document", { taskDuid }),
    getMostRecentCommentForTasks: (
      taskDuids: string[],
      publicViewDuid?: string
    ): Response<{ result: Record<string, Comment | null> }> => {
      const params = new URLSearchParams();
      params.append("task_duids", taskDuids.join(","));
      let url = "tasks/get-most-recent-comment-for-tasks";
      if (publicViewDuid) {
        params.append("view_duid", publicViewDuid);
        url = "tasks/get-public-view-most-recent-comment-for-tasks";
      }
      return backendOld._get(`${url}?${params.toString()}`, {
        background: true,
        authOverride: publicViewDuid ? true : undefined,
      });
    },
    queryRelationships: (items: TaskRelationshipQuery[]): Response<{ result: Record<string, Task[] | null> }> =>
      backendOld._post("tasks/query-relationships", { items }, { background: true }),
    emptyTrash: () => backendOld._get("tasks/empty-trash"),
  },
  recommendations: {
    provideFeedback: (duid: string, rating: FeedbackRating) =>
      backendOld._post(`recommendations/feedback/${duid}`, { rating }),
    provideTaskDuplicatesFeedback: (taskDuid: string, duplicateDuids: string[], rating: FeedbackRating) =>
      backendOld._post("recommendations/task-duplicates-feedback", { taskDuid, duplicateDuids, rating }),
    getTaskDuplicates: (taskDuid: string) => backendOld._get(`recommendations/task-duplicates/${taskDuid}`),
    getTaskProperties: (duid: string, fields: TaskField[], newTitle?: string | null) =>
      backendOld._post(`recommendations/task-properties/${duid}`, { newTitle: newTitle ?? null, fields }),
    getFilters: (text: string, includeQuery?: boolean) =>
      backendOld._post("recommendations/filters", { text, includeQuery }),
    getFiltersSummary: (layoutDuid: string) => backendOld._post("recommendations/filters-summary", { layoutDuid }),
    getPageEmoji: (duid: string, newTitle?: string | null) =>
      backendOld._post(`recommendations/page-emoji/${duid}`, { newTitle: newTitle ?? null }),
    getPageIcon: (duid: string, newTitle?: string | null) =>
      backendOld._post(`recommendations/page-icon/${duid}`, { newTitle: newTitle ?? null }),
    startPlanningProject: (
      dartboardDuid: string,
      selectedTaskDuids: string[],
      options: string[],
      startDate: string | null,
      endDate: string | null,
      assigneeDuids: string[]
    ) =>
      backendOld._post(`recommendations/start-planning-project/${dartboardDuid}`, {
        selectedTaskDuids,
        options,
        startDate,
        endDate,
        assigneeDuids,
      }),
    streamSubtasks: (duid: string, newTitle?: string | null) =>
      backendOld._post(`recommendations/stream/task-subtasks/${duid}`, { newTitle: newTitle ?? null }),
    streamContent: (duid: string, kind: RecommendationKind, onStream: StreamCallback, newTitle?: string | null) =>
      backendOld._post(
        `recommendations/stream/content/${kind}/${duid}`,
        { newTitle: newTitle ?? null },
        {},
        {},
        onStream
      ),
    streamTranslation: (duid: string, language: Language, onStream: StreamCallback, newTitle?: string | null) =>
      backendOld._post(
        `recommendations/stream/translate/${language}/${duid}`,
        { newTitle: newTitle ?? null },
        {},
        {},
        onStream
      ),
    streamReport: (docDuid: string, startDate: string, endDate: string, onStream: StreamCallback) =>
      backendOld._post(`recommendations/stream/report/${docDuid}`, { startDate, endDate }, {}, {}, onStream),
  },
  chat: {
    streamMessage: (message: ChatMessage, chatDuid: string, onStream: StreamCallback) =>
      backendOld._post(`chats/${chatDuid}/send`, { message }, {}, {}, onStream),
  },
  speechToText: {
    transcribe: (audio: Blob) => backendOld._post("voice/transcribe", audio, {}, { "Content-Type": AUDIO_MIME_TYPE }),
  },
  profile: {
    changePassword: (newPassword: string, oldPassword?: string) =>
      backendOld._post("profiles/change-password", { newPassword, oldPassword: oldPassword ?? null }),
    rotateAuthToken: () => backendOld._post("profiles/rotate-auth-token"),
    deleteAuthToken: () => backendOld._post("profiles/delete-auth-token"),
    rotateCalendarLink: () => backendOld._post("profiles/rotate-calendar-link"),
    deleteCalendarLink: () => backendOld._post("profiles/delete-calendar-link"),
    updateTutorialStatuses: (updates: { name: TutorialName; status: number }[]) =>
      backendOld._post("profiles/update-tutorial-statuses", { items: updates }),
    edit: (field: string, value: string | boolean | number) =>
      backendOld._post("profiles/edit", { items: [{ field, value }] }),
    uploadImage: async (file: File) => {
      const response = await backendOld.createPresignedUrl("profile", file.name);
      await backendOld._put(response.data.presignedUrl, file, { absolute: true }, response.data.signedHeaders);
      await backendOld._post("profiles/complete-image-upload", {
        imagePath: response.data.filePath,
        contentType: file.type,
      });
      return response;
    },
    removeImage: () => backendOld._post("profiles/remove-image"),
    contactSupport: (text: SerializedEditorState) => backendOld._post("profiles/contact-support", { text }),
    deleteAccount: () => backendOld._post("profiles/delete"),
  },
  workspace: {
    edit: (field: string, value: string | boolean | null) =>
      backendOld._post("workspaces/edit", { items: [{ field, value }] }),
    rotateWebhookSecret: () => backendOld._post("workspaces/rotate-webhook-secret"),
    finishOnboarding: (name: string, adtl: Record<string, string | null>) =>
      backendOld._post("workspaces/edit", {
        items: [
          { field: "name", value: name },
          { field: "adtl", value: adtl },
        ],
      }),
    uploadImage: async (file: File) => {
      const response = await backendOld.createPresignedUrl("workspace", file.name);
      await backendOld._put(response.data.presignedUrl, file, { absolute: true }, response.data.signedHeaders);
      await backendOld._post("workspaces/complete-image-upload", {
        imagePath: response.data.filePath,
        contentType: file.type,
      });
      return response;
    },
    enableSamlSso: () => backendOld._post("workspaces/sso/enable-saml"),
    disableSamlSso: () => backendOld._post("workspaces/sso/disable-saml"),
    configureSamlSso: (options: { url?: string; xml?: string }) =>
      backendOld._post("workspaces/sso/configure-saml", { url: options.url ?? null, xml: options.xml ?? null }),
    removeImage: () => backendOld._post("workspaces/remove-image"),
    inviteUsers: (role: UserRole, emails: string[]) => backendOld._post("workspaces/invite-users", { role, emails }),
    reinviteUsers: (duids: string[]) => backendOld._post("workspaces/reinvite-users", { duids }),
    removeUsers: (duids: string[]) => backendOld._post("workspaces/remove-users", { duids }),
    import: (data: CSVImportData, source?: string) =>
      backendOld._post("workspaces/import", { data, source: source ?? null }),
    export: () => backendOld._post("workspaces/export"),
  },
  billing: {
    createCheckoutSession: (tier: string, period: string, returnPath: string) =>
      backendOld._post("billing/create-checkout-session", { tier, period, returnPath }),
    createPortalSession: (returnPath: string) => backendOld._post("billing/create-portal-session", { returnPath }),
    updateSubscriptionTier: (tier: string, period: string, returnPath: string) =>
      backendOld._post("billing/update-subscription-tier", { tier, period, returnPath }),
    resetAccount: () => backendOld._post("billing/reset-account"),
    deleteSubscriptions: () => backendOld._post("billing/delete-subscriptions"),
  },
  notifications: {
    edit: (edits: NotificationSettingEdit[]) => backendOld._post("notifications/edit", { items: edits }),
    markAllRead: (): Response<{ success: boolean }> => backendOld._post("notifications/mark-all-read"),
  },
  notion: {
    enableIntegration: (code: string | null) => backendOld._post("notion/enable", { code }),
    disableIntegration: () => backendOld._post("notion/disable"),
  },
  slack: {
    enableIntegration: () => backendOld._post("slack/enable"),
    getInstallationLink: () => `${BASE_URL}/slack/install`,
    disableIntegration: () => backendOld._post("slack/disable"),
  },
  discord: {
    edit: (field: "enabled" | "webhookUrl", value: string | boolean | null) =>
      backendOld._post("discord/edit", { items: [{ field, value }] }),
  },
  zapier: {
    edit: (field: "enabled", value: boolean) => backendOld._post("zapier/edit", { items: [{ field, value }] }),
  },
  vcs: {
    enableGithubIntegration: (installationId: string | null) =>
      backendOld._post("vcs/github/enable", { installation_id: installationId }),
    disableGithubIntegration: () => backendOld._post("vcs/github/disable"),
    updateGithubSettings: (edits: GithubIntegrationEdit[]) => backendOld._post("vcs/github/edit", { items: edits }),
    onBranchLinkCopy: (taskDuid: string) => backendOld._post("vcs/copy-branch-link", { duid: taskDuid }),
  },
  entityList: {
    queryTasks: (
      filterGroup: FilterGroup,
      sorts: Sort[],
      search: string,
      offset: number,
      options?: {
        hierarchical?: boolean;
        absentees?: boolean;
        groupByPropertyDuid?: string | null;
        limit?: number;
        onlyTrash?: boolean;
        includeTrash?: boolean;
      }
    ): Response<PaginatedTaskList> =>
      backendOld._post(
        "tasks/query",
        {
          filterGroup,
          sorts,
          search,
          pagination: { offset, limit: options?.limit },
          hierarchical: options?.hierarchical ?? false,
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
          onlyTrash: options?.onlyTrash ?? false,
          includeTrash: options?.includeTrash ?? false,
        },
        { background: true }
      ),
    getPublicViewTasks: (
      viewDuid: string,
      extraFilterGroup: FilterGroup,
      offset: number,
      options?: { hierarchical?: boolean; absentees?: boolean; groupByPropertyDuid?: string | null; limit?: number }
    ): Response<PaginatedTaskList> =>
      backendOld._post(
        "tasks/get-public-view-tasks",
        {
          viewDuid,
          extraFilterGroup,
          pagination: { offset, limit: options?.limit },
          hierarchical: options?.hierarchical ?? false,
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
        },
        { background: true, authOverride: true }
      ),
    getSummaryStatistics: (
      filterGroup: FilterGroup,
      options?: {
        search?: string;
        absentees?: boolean;
        groupByPropertyDuid?: string | null;
        onlyTrash?: boolean;
        includeTrash?: boolean;
      }
    ): Response<SummaryStatisticsResponse> =>
      backendOld._post(
        "tasks/summary-statistics",
        {
          filterGroup,
          search: options?.search ?? "",
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
          onlyTrash: options?.onlyTrash ?? false,
          includeTrash: options?.includeTrash ?? false,
        },
        { background: true }
      ),
    getPublicViewSummaryStatistics: (
      viewDuid: string,
      extraFilterGroup: FilterGroup,
      options?: { absentees?: boolean; groupByPropertyDuid?: string | null }
    ): Response<SummaryStatisticsResponse> =>
      backendOld._post(
        "tasks/get-public-view-summary-statistics",
        {
          viewDuid,
          extraFilterGroup,
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
        },
        { background: true, authOverride: true }
      ),
    comment: (
      filters: {
        authorDuid?: string;
        order?: string;
        isDraft?: boolean;
        rootDuid?: string | null;
        taskDuid?: string;
      },
      offset: number,
      limit?: number
    ): Response<PaginatedCommentList> => {
      const appStore = useAppStore();
      const pageStore = usePageStore();
      const publicViewDuid = pageStore.isPublicView && appStore.currentPage ? appStore.currentPage.duid : undefined;
      let url = "comments";
      if (publicViewDuid) {
        url = "tasks/get-public-view-comments";
      }

      const { taskDuid, authorDuid, rootDuid, isDraft, order } = filters;
      return backendOld._get(
        paginatedUrl(url, offset, limit, {
          author_duid: authorDuid,
          o: order,
          is_draft: isDraft,
          root_duid: rootDuid,
          task_duid: taskDuid,
          view_duid: publicViewDuid,
        }),
        { background: true, authOverride: publicViewDuid ? true : undefined }
      );
    },
    attachment: (taskDuid: string, offset: number, limit?: number): Response<PaginatedAttachmentList> => {
      const appStore = useAppStore();
      const pageStore = usePageStore();
      const publicViewDuid = pageStore.isPublicView && appStore.currentPage ? appStore.currentPage.duid : undefined;
      let url = "attachments";
      if (publicViewDuid) {
        url = "tasks/get-public-view-attachments";
      }

      return backendOld._get(
        paginatedUrl(url, offset, limit, {
          task_duid: taskDuid,
          view_duid: publicViewDuid,
        }),
        { background: true, authOverride: publicViewDuid ? true : undefined }
      );
    },
    doc: (
      filters: { folderDuid?: string; search?: string; order?: string; reportKind?: ReportKind },
      offset: number,
      limit?: number
    ): Response<PaginatedDocList> => {
      const { search, folderDuid, order, reportKind } = filters;
      return backendOld._get(
        paginatedUrl("docs", offset, limit, { folder_duid: folderDuid, report_kind: reportKind, s: search, o: order }),
        { background: true }
      );
    },
    task: (
      filters: { title?: string; inTrash?: boolean; isDraft?: boolean },
      offset: number,
      limit?: number
    ): Response<PaginatedTaskList> => {
      const { title, inTrash, isDraft } = filters;
      return backendOld._get(paginatedUrl("tasks", offset, limit, { title, in_trash: inTrash, is_draft: isDraft }), {
        background: true,
      });
    },
    notification: (
      filters: { read?: boolean },
      offset: number,
      limit?: number
    ): Response<PaginatedNotificationList> => {
      const { read } = filters;
      return backendOld._get(paginatedUrl("notifications", offset, limit, { read }), { background: true });
    },
  },
};

export default backendOld;
